import { Controller } from "@hotwired/stimulus"
import { showSystemToast } from "../utils/system_toast"

// Save only fields changed in this form. Requests are serialized so an older
// response can never overwrite a newer edit; failures keep the draft in place.
export default class extends Controller {
  static targets = ["status", "retry"]
  static values = { param: { type: String, default: "profile_page" }, message: { type: String, default: "Page saved" } }

  connect() {
    this.confirmed = this.snapshot()
    this._beforeUnload = (event) => {
      if (!this.changedNames(this.snapshot()).length) return
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", this._beforeUnload)
  }

  disconnect() {
    clearTimeout(this.timer)
    window.removeEventListener("beforeunload", this._beforeUnload)
  }

  schedule(event) {
    if (!event.target.name) return
    clearTimeout(this.timer)
    this.say("Unsaved changes…")
    this.timer = setTimeout(() => this.save(), 600)
  }

  change(event) {
    if (!event.target.name) return
    clearTimeout(this.timer)
    this.save()
  }

  submit(event) {
    event.preventDefault()
    clearTimeout(this.timer)
    this.save()
  }

  snapshot() {
    const snapshot = new Map()
    for (const [name, value] of new FormData(this.element)) {
      if (!name.startsWith(`${this.paramValue}[`)) continue
      if (!snapshot.has(name)) snapshot.set(name, [])
      snapshot.get(name).push(value)
    }
    return snapshot
  }

  changedNames(snapshot) {
    return [...snapshot.keys()].filter(name => JSON.stringify(snapshot.get(name)) !== JSON.stringify(this.confirmed.get(name)))
  }

  async flush() {
    clearTimeout(this.timer)
    while (this.saving) {
      await new Promise(resolve => this.element.addEventListener("profile-autosave:idle", resolve, { once: true }))
    }
    await this.save()
    return this.changedNames(this.snapshot()).length === 0
  }

  async save() {
    if (this.saving) { this.pending = true; return }
    const snapshot = this.snapshot()
    const changed = this.changedNames(snapshot)
    if (!changed.length) return
    // These collections are replaced by Rails, so send the entire collection.
    const groups = ["links", "personal_info", "shared_contact"].filter(group => changed.some(name => name.startsWith(`profile_page[${group}]`)))
    const names = [...snapshot.keys()].filter(name => changed.includes(name) || groups.some(group => name.startsWith(`profile_page[${group}]`)))
    // Keep the displayed default authoritative when enabling a previously
    // hidden legacy view whose stored default may differ from the fallback.
    const homeName = "profile_page[home_view]"
    if (this.element.matches('[data-controller~="page-views"]') && snapshot.has(homeName) &&
        changed.some(name => name.startsWith("profile_page[view_enabled]") || /^profile_page\[section_visibility\]\[(posts|answers|background)\]$/.test(name)) &&
        !names.includes(homeName)) names.push(homeName)
    const body = new FormData()
    body.append("_method", "patch")
    for (const name of names) for (const value of snapshot.get(name)) body.append(name, value)
    this.saving = true
    this.pending = false
    this.say("Saving…")
    if (this.hasRetryTarget) this.retryTarget.hidden = true
    let succeeded = false
    try {
      const response = await fetch(this.element.action, {
        method: "POST", credentials: "same-origin", body,
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content || "" }
      })
      const data = await response.json()
      if (!response.ok || !data.ok) throw new Error(data.errors?.join(". ") || "Could not save your page.")
      for (const name of names) this.confirmed.set(name, snapshot.get(name))
      succeeded = true
      this.say("Saved just now")
      showSystemToast(this.messageValue, { subtitle: "Your changes are live.", duration: 1800 })
      if (data.link) {
        const row = document.getElementById(`profile-link-${data.link.id}`)
        if (row) {
          const icon = row.querySelector(".profile-link-icon")
          if (icon && data.link_icon_html) icon.outerHTML = data.link_icon_html
          for (const [key, value] of Object.entries({ label: data.link.label, url: data.link.url, audience: data.link.audience_label, topic: data.link.topic_label || "" })) {
            const label = row.querySelector(`[data-link-summary-${key}]`)
            if (label) {
              label.textContent = value
              if (key === "topic") label.dataset.profileTopic = data.link.topic || ""
            }
          }
        }
      }
      if (data.answer) {
        const row = document.getElementById(`profile-answer-${data.answer.id}`) || document.querySelector(`[data-profile-answer-summary-id="${data.answer.id}"]`)
        if (row) {
          const question = row.querySelector(".profile-answer-question, [data-answer-summary-question]")
          const text = row.querySelector("[data-answer-summary-text]")
          const audience = row.querySelector("[data-answer-summary-audience]")
          const topic = row.querySelector("[data-answer-summary-topic]")
          if (question) question.textContent = data.answer.question
          if (text) text.textContent = data.answer.text
          if (topic) { topic.textContent = data.answer.topic || ""; topic.dataset.profileTopic = data.answer.topic_selection || "" }
          if (audience) {
            audience.textContent = data.answer.audience
            if (audience.previousElementSibling) audience.previousElementSibling.className = `bi me-1 ${data.answer.icon}`
          }
        }
      }
      if (data.qr_svg) {
        document.querySelectorAll("[data-profile-page-qr]").forEach(node => { node.innerHTML = data.qr_svg })
      }
      if (data.profile_url) {
        document.querySelectorAll(".profile-editor-share").forEach(node => { node.dataset.shareBtn = data.profile_url })
        document.querySelectorAll("[data-profile-page-url]").forEach(node => {
          if (node.tagName === "A") node.href = data.profile_url
          else node.textContent = data.profile_url
        })
      }
    } catch (error) {
      this.say(`${error.message} Your changes have not been saved.`, true)
      if (this.hasRetryTarget) this.retryTarget.hidden = false
      showSystemToast("Could not save your page", { level: "danger", subtitle: "Your draft is still here. Retry when ready.", duration: 5000 })
    } finally {
      this.saving = false
      this.element.dispatchEvent(new Event("profile-autosave:idle"))
      if (this.pending || (succeeded && this.changedNames(this.snapshot()).length)) this.save()
    }
  }

  say(text, error = false) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("text-danger", error)
  }
}
